import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listenerGreetings, listenerSongRequests, greetingStatusLabels, greetingStatusColors, songStatusLabels, songStatusColors, type ListenerGreeting, type ListenerSongRequest, type GreetingStatus, type SongRequestStatus } from "@/mocks/listenerGreetingsAndSongs";
import Swal from "sweetalert2";

/* ── Tabs ── */
type MainTab = "greetings" | "songs";
type GreetingFilter = "all" | "pending" | "aired" | "archived";
type SongFilter = "all" | "pending" | "accepted" | "attended" | "played" | "rejected";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function timeAgo(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return "Ahora";
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  return `Hace ${Math.floor(diffHrs / 24)}d`;
}

/* ── Send Modal ── */
function SendModal({ mode, onClose }: { mode: "greeting" | "song"; onClose: () => void }) {
  const [greetingText, setGreetingText] = useState("");
  const [songName, setSongName] = useState("");
  const [artistName, setArtistName] = useState("");
  const [dedication, setDedication] = useState("");

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "greeting" && !greetingText.trim()) return;
    if (mode === "song" && !songName.trim() && !dedication.trim() && !greetingText.trim()) return;
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: mode === "greeting" ? "¡Saludo enviado!" : "¡Canción solicitada!",
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
    onClose();
  }, [mode, greetingText, songName, artistName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-background-50 rounded-t-3xl sm:rounded-3xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground-950">
            {mode === "greeting" ? "Enviar Saludo" : "Solicitar Canción"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "greeting" ? (
            <div>
              <label className="block text-xs font-bold text-foreground-600 mb-1.5">Tu mensaje</label>
              <textarea
                value={greetingText}
                onChange={(e) => setGreetingText(e.target.value)}
                placeholder="Escribí tu saludo para que el locutor lo lea al aire..."
                maxLength={500}
                rows={4}
                className="w-full px-3.5 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 resize-none"
              ></textarea>
              <p className="text-[10px] text-foreground-400 mt-1 text-right">{greetingText.length}/500</p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold text-foreground-600 mb-1.5">Nombre de la canción</label>
                <input
                  type="text"
                  value={songName}
                  onChange={(e) => setSongName(e.target.value)}
                  placeholder="Ej: Vivir Mi Vida"
                  maxLength={100}
                  className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-foreground-600 mb-1.5">Artista (opcional)</label>
                <input
                  type="text"
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Ej: Marc Anthony"
                  maxLength={100}
                  className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-foreground-600 mb-1.5">Dedicatoria (opcional)</label>
                <textarea
                  value={dedication}
                  onChange={(e) => setDedication(e.target.value)}
                  placeholder="¿A quién va dedicada la canción?"
                  maxLength={200}
                  rows={2}
                  className="w-full px-3.5 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-300 resize-none"
                ></textarea>
                <p className="text-[10px] text-foreground-400 mt-1 text-right">{dedication.length}/200</p>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={mode === "greeting" ? !greetingText.trim() : (!songName.trim() && !dedication.trim() && !greetingText.trim())}
            className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-background-50 font-bold text-sm cursor-pointer whitespace-nowrap transition-all"
          >
            <i className={`${mode === "greeting" ? "ri-send-plane-fill" : "ri-music-fill"} mr-1.5`}></i>
            {mode === "greeting" ? "Enviar Saludo" : "Solicitar Canción"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Greeting Card ── */
function GreetingCard({ greeting }: { greeting: ListenerGreeting }) {
  const status = greetingStatusColors[greeting.status];
  const label = greetingStatusLabels[greeting.status];

  return (
    <div className={`rounded-2xl border ${status.bg} p-4 transition-all`}>
      {/* Radio header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img src={greeting.radioImage} alt={greeting.radioName} className="w-full h-full object-cover object-top" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-foreground-950">{greeting.radioName}</h4>
          <p className="text-[10px] text-foreground-500">{greeting.radioFrequency}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
          <span className={`text-[10px] font-bold ${status.text}`}>{label}</span>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm text-foreground-800 leading-relaxed mb-2">"{greeting.content}"</p>

      {/* Status details */}
      <div className="flex items-center justify-between text-[10px] text-foreground-400">
        <span>Enviado {timeAgo(greeting.sentAt)}</span>
        {greeting.readAt && <span>Leído {timeAgo(greeting.readAt)}</span>}
      </div>

      {/* Locutor response */}
      {greeting.locutorResponse && (
        <div className="mt-3 p-3 rounded-xl bg-background-50 border border-rose-200">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-message-2-fill text-rose-500 text-xs"></i>
            <span className="text-[11px] font-bold text-rose-600">Respuesta del locutor</span>
          </div>
          <p className="text-sm text-foreground-800 italic">"{greeting.locutorResponse}"</p>
          {greeting.respondedAt && (
            <p className="text-[10px] text-foreground-400 mt-1">Respondido {timeAgo(greeting.respondedAt)}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Song Request Card ── */
function SongCard({ song }: { song: ListenerSongRequest }) {
  const status = songStatusColors[song.status];
  const label = songStatusLabels[song.status];

  return (
    <div className={`rounded-2xl border ${status.bg} p-4 transition-all`}>
      {/* Radio header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img src={song.radioImage} alt={song.radioName} className="w-full h-full object-cover object-top" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-foreground-950">{song.radioName}</h4>
          <p className="text-[10px] text-foreground-500">{song.radioFrequency}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
          <span className={`text-[10px] font-bold ${status.text}`}>{label}</span>
        </div>
      </div>

      {/* Song info */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0">
          <i className={`${status.icon} ${status.text} text-lg`}></i>
        </div>
        <div className="min-w-0 flex-1">
          {song.songName ? (
            <>
              <h4 className="text-sm font-bold text-foreground-950 truncate">{song.songName}</h4>
              {song.artistName && <p className="text-xs text-foreground-600">{song.artistName}</p>}
            </>
          ) : (
            <h4 className="text-sm font-bold text-foreground-950">Solo saludo</h4>
          )}
          {song.greeting && (
            <p className="text-[11px] text-foreground-600 mt-1">"{song.greeting}"</p>
          )}
          {song.dedication && (
            <p className="text-[11px] text-foreground-500 italic mt-1">"{song.dedication}"</p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex items-center justify-between text-[10px] text-foreground-400 mt-3 pt-3 border-t border-foreground-200/50">
        <span>Solicitada {timeAgo(song.requestedAt)}</span>
        {song.acceptedAt && <span>Aceptada {timeAgo(song.acceptedAt)}</span>}
        {song.attendedAt && <span className="text-sky-600 font-semibold">Atendida {timeAgo(song.attendedAt)}</span>}
        {song.playedAt && <span className="text-emerald-600 font-semibold">Transmitida {timeAgo(song.playedAt)}</span>}
      </div>

      {/* Operator response */}
      {song.operatorResponse && (
        <div className="mt-3 p-2.5 rounded-xl bg-sky-50 border border-sky-200">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-message-2-fill text-sky-500 text-xs"></i>
            <span className="text-[11px] font-bold text-sky-600">Respuesta del locutor</span>
          </div>
          <p className="text-sm text-foreground-800 italic">"{song.operatorResponse}"</p>
        </div>
      )}

      {/* Rejection reason */}
      {song.status === "rechazada" && song.rejectionReason && (
        <div className="mt-3 p-2.5 rounded-xl bg-red-50/80 border border-red-200">
          <p className="text-[11px] text-red-700">
            <i className="ri-information-line text-xs mr-1"></i>
            {song.rejectionReason}
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function SaludosCancionesPage() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState<MainTab>("greetings");
  const [greetingFilter, setGreetingFilter] = useState<GreetingFilter>("all");
  const [songFilter, setSongFilter] = useState<SongFilter>("all");
  const [showSendModal, setShowSendModal] = useState<"greeting" | "song" | null>(null);

  const filteredGreetings = greetingFilter === "all" ? listenerGreetings
    : greetingFilter === "pending" ? listenerGreetings.filter((g) => g.status === "enviado" || g.status === "leido_por_locutor")
    : greetingFilter === "aired" ? listenerGreetings.filter((g) => g.status === "al_aire")
    : listenerGreetings.filter((g) => g.status === "archivado");

  const filteredSongs = songFilter === "all" ? listenerSongRequests
    : songFilter === "pending" ? listenerSongRequests.filter((s) => s.status === "pendiente" || s.status === "aceptada")
    : songFilter === "attended" ? listenerSongRequests.filter((s) => s.status === "atendida")
    : songFilter === "played" ? listenerSongRequests.filter((s) => s.status === "reproducida")
    : listenerSongRequests.filter((s) => s.status === "rechazada");

  const mainTabs = [
    { id: "greetings" as MainTab, label: "Mis Saludos", icon: "ri-chat-smile-2-line", count: listenerGreetings.length, color: "text-rose-500" },
    { id: "songs" as MainTab, label: "Mis Canciones", icon: "ri-music-line", count: listenerSongRequests.length, color: "text-violet-500" },
  ];

  const greetingFilters: { key: GreetingFilter; label: string; count: number }[] = [
    { key: "all", label: "Todos", count: listenerGreetings.length },
    { key: "pending", label: "Pendientes", count: listenerGreetings.filter((g) => g.status === "enviado" || g.status === "leido_por_locutor").length },
    { key: "aired", label: "Al Aire", count: listenerGreetings.filter((g) => g.status === "al_aire").length },
    { key: "archived", label: "Archivados", count: listenerGreetings.filter((g) => g.status === "archivado").length },
  ];

  const songFilters: { key: SongFilter; label: string; count: number }[] = [
    { key: "all", label: "Todas", count: listenerSongRequests.length },
    { key: "pending", label: "Pendientes", count: listenerSongRequests.filter((s) => s.status === "pendiente" || s.status === "aceptada").length },
    { key: "attended", label: "Atendidas", count: listenerSongRequests.filter((s) => s.status === "atendida").length },
    { key: "played", label: "Transmitidas", count: listenerSongRequests.filter((s) => s.status === "reproducida").length },
    { key: "rejected", label: "Rechazadas", count: listenerSongRequests.filter((s) => s.status === "rechazada").length },
  ];

  return (
    <div className="fixed inset-0 flex flex-col bg-background-50 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 md:px-6 pt-4 pb-2 border-b border-background-100">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate("/app")} className="w-9 h-9 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer shrink-0 transition-all">
            <i className="ri-arrow-left-line text-foreground-600"></i>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-foreground-950">Saludos y Canciones</h1>
            <p className="text-[11px] text-foreground-400">Tus interacciones con las radios</p>
          </div>
          <button
            onClick={() => setShowSendModal(mainTab === "greetings" ? "greeting" : "song")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-bold cursor-pointer whitespace-nowrap transition-all shrink-0"
          >
            <i className="ri-add-line"></i>
            {mainTab === "greetings" ? "Enviar Saludo" : "Pedir Canción"}
          </button>
        </div>

        {/* Main tabs */}
        <div className="flex gap-1.5 mb-3">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setMainTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                mainTab === tab.id
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              <i className={`${tab.icon} ${mainTab === tab.id ? "text-background-50" : tab.color} text-[13px]`}></i>
              {tab.label}
              <span className="text-[10px] ml-0.5">({tab.count})</span>
            </button>
          ))}
        </div>

        {/* Sub filters */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
          {mainTab === "greetings" && greetingFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setGreetingFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                greetingFilter === f.key
                  ? "bg-rose-100 text-rose-700"
                  : "bg-background-100 text-foreground-400 hover:bg-background-200"
              }`}
            >
              {f.label}
              <span className="ml-1 text-[10px] opacity-70">({f.count})</span>
            </button>
          ))}
          {mainTab === "songs" && songFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setSongFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                songFilter === f.key
                  ? "bg-violet-100 text-violet-700"
                  : "bg-background-100 text-foreground-400 hover:bg-background-200"
              }`}
            >
              {f.label}
              <span className="ml-1 text-[10px] opacity-70">({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-none px-4 md:px-6 py-4">
        {mainTab === "greetings" && (
          filteredGreetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center mb-4">
                <i className="ri-chat-smile-2-line text-rose-300 text-2xl"></i>
              </div>
              <p className="text-foreground-600 text-sm font-medium">Sin saludos</p>
              <p className="text-foreground-400 text-xs mt-1">¡Enviá tu primer saludo a una radio!</p>
              <button
                onClick={() => setShowSendModal("greeting")}
                className="mt-4 px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
              >
                Enviar Saludo
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredGreetings.map((greeting) => (
                <GreetingCard key={greeting.id} greeting={greeting} />
              ))}
            </div>
          )
        )}

        {mainTab === "songs" && (
          filteredSongs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-violet-50 flex items-center justify-center mb-4">
                <i className="ri-music-line text-violet-300 text-2xl"></i>
              </div>
              <p className="text-foreground-600 text-sm font-medium">Sin canciones</p>
              <p className="text-foreground-400 text-xs mt-1">¡Solicitá tu primera canción!</p>
              <button
                onClick={() => setShowSendModal("song")}
                className="mt-4 px-4 py-2 rounded-full bg-violet-500 hover:bg-violet-600 text-white text-xs font-bold cursor-pointer whitespace-nowrap transition-all"
              >
                Solicitar Canción
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSongs.map((song) => (
                <SongCard key={song.id} song={song} />
              ))}
            </div>
          )
        )}
      </div>

      {/* Send Modal */}
      {showSendModal && (
        <SendModal mode={showSendModal} onClose={() => setShowSendModal(null)} />
      )}
    </div>
  );
}